// modules/inicio/context/AuthContext.js
// Contexto de autenticación. Expone `user` con { id, nombre, rol, programa },
// siguiendo el mismo patrón useAuth() del SI-PIC.
//
// CRÍTICO para esta app: se usa en territorio con conectividad intermitente
// (o sin señal por días). Por eso:
//   - El usuario se guarda en localStorage junto al token — al abrir la app
//     sin internet, se restaura la sesión DE INMEDIATO desde ahí, sin
//     esperar ninguna respuesta del servidor.
//   - La verificación contra el servidor pasa en segundo plano, y solo
//     cierra la sesión si el servidor responde 401 (token de verdad
//     inválido/expirado) — un fallo de red (sin respuesta del servidor) NO
//     cierra la sesión bajo ninguna circunstancia. Antes, cualquier error
//     (incluido "no hay señal") borraba el token — ese era el bug real que
//     deslogueaba a la gente en campo.

import { createContext, useContext, useState, useEffect } from "react";
import { loginAPI, obtenerPerfilAPI } from "../api/authAPI";

const AuthContext = createContext(null);

function leerUsuarioCacheado() {
  try {
    const raw = localStorage.getItem("usuario");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(leerUsuarioCacheado());
  // Si ya había token+usuario en caché, no hay que "esperar" nada para
  // mostrar la app — solo se muestra el loader si de verdad no hay nada
  // guardado localmente.
  const [cargando, setCargando] = useState(!leerUsuarioCacheado() && !!localStorage.getItem("token"));

  function guardarSesion(token, usuario) {
    localStorage.setItem("token", token);
    localStorage.setItem("usuario", JSON.stringify(usuario));
    setUser(usuario);
  }

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCargando(false);
      return;
    }

    // Verificación en segundo plano. NO bloquea la app (ya se restauró el
    // usuario cacheado arriba, si había uno) — solo refresca los datos si
    // hay señal, y solo desloguea si el servidor de verdad dice 401.
    obtenerPerfilAPI()
      .then((usuario) => {
        localStorage.setItem("usuario", JSON.stringify(usuario));
        setUser(usuario);
      })
      .catch((error) => {
        if (error?.response?.status === 401) {
          // Token realmente inválido/expirado — ahí sí toca cerrar sesión.
          localStorage.removeItem("token");
          localStorage.removeItem("usuario");
          setUser(null);
        }
        // Cualquier otro error (sin conexión, servidor caído, timeout...)
        // se ignora a propósito — la sesión se queda como estaba.
      })
      .finally(() => setCargando(false));
  }, []);

  async function login(identificacion, password) {
    const { token, usuario } = await loginAPI(identificacion, password);
    guardarSesion(token, usuario);
    return usuario;
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    setUser(null);
  }

  const value = { user, setUser, login, logout, cargando };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe usarse dentro de un <AuthProvider>");
  }
  return ctx;
}
